import moment from 'moment'

// The effective date window for a patient chart.
//
// Every one of these charts defaults to "the last N days" anchored on TODAY,
// and drops records outside it. For a patient who stopped syncing months ago
// that renders an EMPTY chart — while the patient list beside it shows their
// latest temperature, pain score, flexion and steps, because the list's
// fetchTracPatchSummary takes the newest record with no date cutoff.
//
// 🔴 The page contradicts itself, and the clinician concludes the data is
// lost. Reported from staging 2026-11-03 on the Temperature chart; the same
// shape exists on cadence, pain score, steps, flexion, extension, leg
// position and the exercise charts.
//
// WIDENING THE DEFAULT RANGE IS NOT THE FIX. It moves the cliff rather than
// removing it: a patient inactive for longer than whatever window you pick
// still shows nothing.
//
// Instead, when the requested window holds no records at all, the window
// ANCHORS TO THE PATIENT'S LATEST RECORD and shows the most recent `days`
// that actually contain data. `isStale` comes back true so the caller can SAY
// so — silently relabelling an axis would trade one misleading screen for
// another, and a clinician must never read four-month-old readings as
// current.
//
// Only fires when the window is genuinely empty. A patient with one reading
// yesterday and one 200 days ago is NOT stale: recent data exists, and the
// old reading stays excluded exactly as before.
//
// `getDate` extracts the timestamp from a record, because these collections
// disagree about the field name — recorded_at, startTime, endTime, date.
export function effectiveWindow(records, days, getDate) {
  const today = moment().startOf('day')
  const fallback = {
    windowStart: moment().subtract(days - 1, 'days').startOf('day'),
    windowEnd: today,
    isStale: false,
  }
  if (!Array.isArray(records) || records.length === 0) return fallback

  let latest = null
  for (let i = 0; i < records.length; i++) {
    const raw = getDate ? getDate(records[i]) : records[i]
    if (!raw) continue
    const m = moment(raw)
    if (!m.isValid()) continue
    if (latest === null || m.isAfter(latest)) latest = m
  }
  if (latest === null) return fallback

  // Something inside the requested window — normal behaviour.
  if (!latest.isBefore(fallback.windowStart)) return fallback

  const windowEnd = latest.clone().startOf('day')
  return {
    windowStart: windowEnd.clone().subtract(days - 1, 'days'),
    windowEnd,
    isStale: true,
  }
}

// The banner shown above a chart whose window fell back. One wording in one
// place, so eight charts cannot each describe this slightly differently.
export function staleRangeLabel(windowStart, windowEnd, days) {
  return (
    'No readings in the last ' +
    days +
    ' days. Showing ' +
    moment(windowStart).format('MMM D, YYYY') +
    ' – ' +
    moment(windowEnd).format('MMM D, YYYY') +
    ', the most recent data available for this patient.'
  )
}

export default { effectiveWindow, staleRangeLabel }
